import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@v2/lib/api';
import { localeTag, translate, type AppTranslations } from '@v2/lib/i18n';
import type { V2LocaleCode } from '@v2/config/locales';

type I18nContextValue = {
    locale: string;
    localeTag: string;
    isLoading: boolean;
    isChangingLocale: boolean;
    t: (key: string, replace?: Record<string, string | number>) => string;
    setLocale: (code: V2LocaleCode) => Promise<void>;
};

const I18nContext = createContext<I18nContextValue | null>(null);

type I18nResponse = {
    locale: string;
    translations: AppTranslations;
};

export default function I18nProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient();
    const bootLocale = window.__V2__?.locale ?? 'pt_BR';
    const [locale, setLocaleState] = useState(bootLocale);

    const { data, isLoading, isFetching } = useQuery({
        queryKey: ['i18n', locale],
        queryFn: async () => {
            const { data: payload } = await api.get<I18nResponse>(`/i18n/${locale}`);
            return payload;
        },
        staleTime: 0,
        gcTime: 0,
    });

    const translations = data?.translations ?? {};

    const t = useCallback(
        (key: string, replace?: Record<string, string | number>) =>
            translate(translations, key, replace),
        [translations],
    );

    const setLocale = useCallback(
        async (code: V2LocaleCode) => {
            if (code === locale) {
                return;
            }

            const { data: saved } = await api.post<{ locale: string }>('/locale', { locale: code });
            const next = saved.locale ?? code;

            if (window.__V2__) {
                window.__V2__.locale = next;
            }

            setLocaleState(next);

            await queryClient.invalidateQueries({ queryKey: ['i18n'] });
            await queryClient.invalidateQueries({ queryKey: ['meta'] });
        },
        [locale, queryClient],
    );

    const value = useMemo(
        () => ({
            locale: data?.locale ?? locale,
            localeTag: localeTag(data?.locale ?? locale),
            isLoading: isLoading || isFetching,
            isChangingLocale: isFetching && !isLoading,
            t,
            setLocale,
        }),
        [data?.locale, locale, isLoading, isFetching, t, setLocale],
    );

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
    const ctx = useContext(I18nContext);
    if (!ctx) {
        throw new Error('useI18n must be used within I18nProvider');
    }
    return ctx;
}
